/**
 * Document recognition of uploaded PDF/scan invoices (backend modules/Accounting,
 * S6/D6): POST /api/e-invoice/{uuid}/extract queues the recognition, GET
 * /api/e-invoice/{uuid}/extraction returns its state and, once done, the unsaved
 * posting proposal; POST /api/e-invoice/{uuid}/extraction/continue repeats only the
 * failed classification or fetches an interrupted provider job. Nothing is booked
 * automatically.
 */
import type { AccountingPostingResource } from "../../accounting-posting";

export enum InvoiceExtractionStatusEnum {
  Queued = "queued",
  Running = "running",
  /** recognition stored, the classification runs as its own step */
  Classifying = "classifying",
  Done = "done",
  /** recognition failed, no recognised data */
  Failed = "failed",
  /** recognition stored, only the classification failed */
  ClassificationFailed = "classification_failed",
}

export type InvoiceExtractionIssue = { code: string; message: string };

export type InvoiceExtractionPlausibility = {
  ok: boolean;
  issues: InvoiceExtractionIssue[];
  needs_classification: boolean;
  hints: string[];
};

export type InvoiceExtractionSemantics = {
  provider: string;
  model: string | null;
  expense_kind: string;
  tax_type: string;
  tax_exemption_type: string | null;
  legal_basis: string | null;
  confidence: number;
  reasoning: string;
  applicable_vat_percent: number | null;
};

export type InvoiceExtractionSupplierMatch = {
  supplier_id: string;
  method: "vat_number" | "name";
  score: number;
  candidate: string;
};

export type InvoiceExtractionSummary = {
  document_number: string | null;
  document_date: string | null;
  service_date: string | null;
  currency: string | null;
  gross_total: number | null;
  net_total: number | null;
  tax_total: number | null;
  vat_lines: { percent: number; basis_amount: number; amount: number }[];
  seller_name: string | null;
  seller_vat_number: string | null;
  iban: string | null;
  positions_count: number;
};

export interface InvoiceExtractionResource {
  id: string;
  media_uuid: string;
  media_file_name: string | null;
  owner_id: string | null;
  status: InvoiceExtractionStatusEnum;
  provider: string | null;
  model: string | null;
  semantic_provider: string | null;
  semantic_model: string | null;
  pages: number | null;
  summary: InvoiceExtractionSummary | null;
  field_confidence: Record<string, number>;
  overall_confidence: number | null;
  warnings: string[];
  plausibility: InvoiceExtractionPlausibility | null;
  semantics: InvoiceExtractionSemantics | null;
  supplier_id: string | null;
  supplier_match: InvoiceExtractionSupplierMatch | null;
  usage: Record<string, unknown> | null;
  error: string | null;
  /** continuable without a new paid recognition (POST …/extraction/continue) */
  can_continue: boolean;
  process_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
  proposal: AccountingPostingResource | null;
  proposal_error: string | null;
}
